import React, { useEffect, useState, useMemo } from "react";
import { Table, Typography, Space, Input, message, Button, Modal, Form, Select } from "antd";
import AxiosOICStat from "AxiosOICStat";
import { useSelector } from 'react-redux';
import { EditOutlined, DeleteOutlined, PlusOutlined } from '@ant-design/icons';

const { Text } = Typography;
const { Search } = Input;
const { Option } = Select;

export default function IloCountryMappingTab() {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [query, setQuery] = useState("");
  const countriesState = useSelector((state) => state.countries);
  const [editModalVisible, setEditModalVisible] = useState(false);
  const [addModalVisible, setAddModalVisible] = useState(false);
  const [editingRecord, setEditingRecord] = useState(null);
  const [editForm] = Form.useForm();
  const [addForm] = Form.useForm();

  useEffect(() => {
    fetchMappings();
  }, []);

  const fetchMappings = async () => {
    setLoading(true);
    try {
      const res = await AxiosOICStat.get("/external_api/ilo/country-mappings");
      setData(res.data.mappings || []);
    } catch (err) {
      const detail =
        err?.response?.data?.detail ||
        err?.message ||
        "Failed to load ILO country mappings. Please try again.";
      message.error(detail);
    } finally {
      setLoading(false);
    }
  };

  const filtered = useMemo(() => {
    if (!query.trim()) return data;

    const q = query.toLowerCase();
    return data.filter((r) => {
      const country = countriesState.value ? countriesState.value[r.c_code] : null;
      const countryName = country && country.c_name_eng ? country.c_name_eng : '';
      
      const iloId = String(r.ilo_id || "").toLowerCase();
      const refArea = String(r.ref_area || "").toLowerCase();
      const refAreaLabel = String(r.ref_area_label || "").toLowerCase();
      const cCode = String(r.c_code || "");
      
      return (
        cCode.includes(q) ||
        iloId.includes(q) ||
        refArea.includes(q) ||
        refAreaLabel.includes(q) ||
        countryName.toLowerCase().includes(q)
      );
    });
  }, [data, query, countriesState.value]);

  const handleEdit = (record) => () => {
    setEditingRecord(record);
    editForm.setFieldsValue({
      c_code: record.c_code,
      ilo_id: record.ilo_id,
      ref_area: record.ref_area,
      ref_area_label: record.ref_area_label
    });
    setEditModalVisible(true);
  };

  const handleDelete = (ref_area) => () => {
    Modal.confirm({
      title: 'Delete ILO Country Mapping',
      content: `Are you sure you want to delete the mapping for ${ref_area}? This action cannot be undone.`,
      okText: 'Yes, Delete',
      okType: 'danger',
      cancelText: 'Cancel',
      onOk: async () => {
        try {
          await AxiosOICStat.delete(`/external_api/ilo/country-mappings/${ref_area}`);
          setData((prevData) => prevData.filter((item) => item.ref_area !== ref_area));
          message.success(`Successfully deleted mapping for ${ref_area}`);
        } catch (err) {
          const detail =
            err?.response?.data?.detail ||
            err?.message ||
            `Failed to delete mapping for ${ref_area}. Please try again.`;
          message.error(detail);
        }
      }
    });
  };

  const handleEditSubmit = async () => {
    try {
      const values = await editForm.validateFields();
      setLoading(true);
      await AxiosOICStat.put(
        `/external_api/ilo/country-mappings/${editingRecord.ref_area}`,
        values
      );
      message.success("Country mapping updated successfully");
      setEditModalVisible(false);
      setEditingRecord(null);
      editForm.resetFields();
      fetchMappings();
    } catch (err) {
      if (err.errorFields) {
        return;
      }
      const detail =
        err?.response?.data?.detail ||
        err?.message ||
        'Failed to update country mapping. Please try again.';
      message.error(detail);
    } finally {
      setLoading(false);
    }
  };

  const handleEditCancel = () => {
    setEditModalVisible(false);
    setEditingRecord(null);
    editForm.resetFields();
  };

  const handleAddSubmit = async () => {
    try {
      const values = await addForm.validateFields();
      setLoading(true);
      await AxiosOICStat.post("/external_api/ilo/country-mappings", values);
      message.success("Country mapping added successfully");
      setAddModalVisible(false);
      addForm.resetFields();
      fetchMappings();
    } catch (err) {
      if (err.errorFields) {
        return;
      }
      const detail =
        err?.response?.data?.detail ||
        err?.message ||
        'Failed to add country mapping. Please try again.';
      message.error(detail);
    } finally {
      setLoading(false);
    }
  };

  const handleAddCancel = () => {
    setAddModalVisible(false);
    addForm.resetFields();
  };

  const columns = [
    {
      title: "ILO Ref Area",
      dataIndex: "ref_area",
      key: "ref_area",
      width: 120,
      render: (val) => val ? <Text code>{val}</Text> : <Text type="secondary">—</Text>,
    },
    {
      title: "Ref Area Label",
      dataIndex: "ref_area_label",
      key: "ref_area_label",
      width: 200,
      render: (val) => val ? <Text>{val}</Text> : <Text type="secondary">—</Text>,
    },
    {
      title: "OIC Country Code",
      dataIndex: "c_code",
      key: "c_code",
      width: 120,
      render: (val) => <Text strong>{val}</Text>,
    },
    {
      title: "ILO ID",
      dataIndex: "ilo_id",
      key: "ilo_id",
      width: 120,
      render: (val) => val ? <Text code>{val}</Text> : <Text type="secondary">—</Text>,
    },
    {
      title: "Actions",
      key: "actions",
      width: 120,
      render: (_, record) => (
        <Space>
          <Button 
            type="primary" 
            icon={<EditOutlined />} 
            size="small"
            style={{ width: 40 }}
            onClick={handleEdit(record)}
          />
          <Button 
            type="primary" 
            danger 
            icon={<DeleteOutlined />} 
            size="small"
            style={{ width: 40 }}
            onClick={handleDelete(record.ref_area)}
          />
        </Space>
      ),
    },
  ];

  const availableCountries = countriesState.value 
    ? Object.values(countriesState.value).sort((a, b) => {
        const aName = a.c_name_eng || '';
        const bName = b.c_name_eng || '';
        return aName.localeCompare(bName);
      })
    : [];

  return (
    <Space direction="vertical" size="middle" style={{ width: "100%" }}>
      <Space>
        <Search
          allowClear
          placeholder="Search by Ref Area / Label / Country Code / ILO ID…"
          onSearch={setQuery}
          onChange={(e) => setQuery(e.target.value)}
          style={{ maxWidth: 420 }}
        />
        <Button 
          type="primary" 
          icon={<PlusOutlined />}
          onClick={() => setAddModalVisible(true)}
        >
          Add New Country Mapping
        </Button>
      </Space>

      <Table
        rowKey="id"
        loading={loading}
        columns={columns}
        dataSource={filtered}
        size="middle"
        pagination={{ pageSize: 20, showSizeChanger: true }}
        bordered
      />

      <Modal
        title="Edit ILO Country Mapping"
        visible={editModalVisible}
        onOk={handleEditSubmit}
        onCancel={handleEditCancel}
        confirmLoading={loading}
      >
        <Form form={editForm} layout="vertical">
          <Form.Item
            name="c_code"
            label="OIC Country Code"
            rules={[{ required: true, message: "Please select a country" }]}
          >
            <Select
              showSearch
              autoClearSearchValue={false}
              placeholder="Select a country"
              filterOption={(input, option) =>
                option.children.toLowerCase().indexOf(input.toLowerCase()) >= 0
              }
            >
              {availableCountries.map(country => (
                <Option key={country.c_code} value={country.c_code}>
                  {country.c_code} - {country.c_name_eng}
                </Option>
              ))}
            </Select>
          </Form.Item>
          <Form.Item
            name="ilo_id"
            label="ILO ID"
            rules={[{ required: true, message: "Please enter ILO ID" }]}
          >
            <Input placeholder="e.g., AFG_A" />
          </Form.Item>
          <Form.Item
            name="ref_area"
            label="Ref Area"
            rules={[{ required: true, message: "Please enter ref area" }]}
          >
            <Input placeholder="e.g., AFG" />
          </Form.Item>
          <Form.Item
            name="ref_area_label"
            label="Ref Area Label"
            rules={[{ required: true, message: "Please enter ref area label" }]}
          >
            <Input placeholder="e.g., Afghanistan" />
          </Form.Item>
        </Form>
      </Modal>

      <Modal
        title="Add ILO Country Mapping"
        visible={addModalVisible}
        onOk={handleAddSubmit}
        onCancel={handleAddCancel}
        confirmLoading={loading}
      >
        <Form form={addForm} layout="vertical">
          <Form.Item
            name="c_code"
            label="OIC Country Code"
            rules={[{ required: true, message: "Please select a country" }]}
          >
            <Select
              showSearch
              placeholder="Select a country"
              filterOption={(input, option) =>
                option.children.toLowerCase().indexOf(input.toLowerCase()) >= 0
              }
            >
              {availableCountries.map(country => (
                <Option key={country.c_code} value={country.c_code}>
                  {country.c_code} - {country.c_name_eng}
                </Option>
              ))}
            </Select>
          </Form.Item>
          <Form.Item
            name="ilo_id"
            label="ILO ID"
            rules={[{ required: true, message: "Please enter ILO ID" }]}
          >
            <Input placeholder="e.g., AFG_A" />
          </Form.Item>
          <Form.Item
            name="ref_area"
            label="Ref Area"
            rules={[{ required: true, message: "Please enter ref area" }]}
          >
            <Input placeholder="e.g., AFG" />
          </Form.Item>
          <Form.Item
            name="ref_area_label"
            label="Ref Area Label"
            rules={[{ required: true, message: "Please enter ref area label" }]}
          >
            <Input placeholder="e.g., Afghanistan" />
          </Form.Item>
        </Form>
      </Modal>
    </Space>
  );
}
